import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import bgImage from '@/assets/Vent_BG-light.png';
import ventLogo from '@/assets/Vent-logo_Primary-tagline-dark.png';

const NAVY = '#16253E';
const MIN_DISPLAY_MS = 600;
// Force-hide the native splash if `isReady` never flips true, so a stuck
// app never leaves the user staring at the splash forever.
const NATIVE_SPLASH_SAFETY_MS = 10000;

interface NativeSplashGateProps {
  isReady: boolean;
  children: React.ReactNode;
}

export const NativeSplashGate = ({ isReady, children }: NativeSplashGateProps) => {
  // Subtle launch sound on native
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const audio = new Audio('/sounds/vent-notification.m4a');
      audio.volume = 0.3;
      audio.play().catch(() => {});
    }
  }, []);

  // Hide the native splash after isReady. Double rAF ensures React has
  // painted at least one frame underneath before the splash tears down,
  // otherwise the unpainted WebView flashes white.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!isReady) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        SplashScreen.hide({ fadeOutDuration: 100 }).catch(() => {});
      });
    });
  }, [isReady]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const t = setTimeout(() => {
      SplashScreen.hide({ fadeOutDuration: 100 }).catch(() => {});
    }, NATIVE_SPLASH_SAFETY_MS);
    return () => clearTimeout(t);
  }, []);

  // Children render under the native splash so React can boot while the
  // user still sees the splash; the effect above unveils them when ready.
  if (Capacitor.isNativePlatform()) {
    return <>{children}</>;
  }

  return <WebSplashGate isReady={isReady}>{children}</WebSplashGate>;
};

const WebSplashGate = ({ isReady, children }: NativeSplashGateProps) => {
  const isBot =
    typeof navigator !== 'undefined' &&
    /Googlebot|bingbot|Twitterbot|facebookexternalhit|GPTBot|PerplexityBot|Slurp|DuckDuckBot|YandexBot/i.test(
      navigator.userAgent,
    );

  const [minTimeElapsed, setMinTimeElapsed] = useState(isBot);
  const [showSplash, setShowSplash] = useState(!isBot);

  useEffect(() => {
    const t = setTimeout(() => setMinTimeElapsed(true), MIN_DISPLAY_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (minTimeElapsed && isReady) {
      const t = setTimeout(() => setShowSplash(false), 300);
      return () => clearTimeout(t);
    }
  }, [minTimeElapsed, isReady]);

  if (!showSplash) return <>{children}</>;

  const isFading = minTimeElapsed && isReady;

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          transition: 'opacity 0.3s ease-out',
          opacity: isFading ? 0 : 1,
          pointerEvents: isFading ? 'none' : 'auto',
        }}
      >
        <img
          src={ventLogo}
          alt="Vent — Designed to help you let it out"
          style={{ width: '70%', maxWidth: 300, height: 'auto' }}
        />
        <div style={{ marginTop: 32 }}>
          <div className="splash-spinner" />
        </div>
        <style>{`
          .splash-spinner {
            width: 28px;
            height: 28px;
            border: 2.5px solid rgba(22, 37, 62, 0.15);
            border-top-color: ${NAVY};
            border-radius: 50%;
            animation: splash-spin 0.8s linear infinite;
          }
          @keyframes splash-spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
      <div style={{ opacity: isFading ? 1 : 0, transition: 'opacity 0.3s ease-out' }}>
        {children}
      </div>
    </>
  );
};
